import {
    DashboardFolderTreeNodeT,
    DashboardT,
    FolderTreeT,
    UserT,
} from 'business/definitions';
import { checkStringMatch } from 'routes/Library/helpers';

/**
 * Recursively retrieves the content of a folder by its ID from the folder tree.
 * @param {FolderTreeT} folderTree - The complete folder tree structure.
 * @param {number | null} currentFolderId - The ID of the folder to retrieve content from.
 */
export const getFolderContent = (
    folderTree: FolderTreeT,
    currentFolderId: number | null,
): FolderTreeT => {
    if (currentFolderId === null) {
        return {
            dashboards: folderTree.dashboards,
            folders: folderTree.folders,
        };
    }

    const findFolder = (
        folders: DashboardFolderTreeNodeT[],
        id: number,
    ): DashboardFolderTreeNodeT | null => {
        for (const folder of folders) {
            if (folder.id === id) return folder;
            const found = findFolder(folder.folders, id);
            if (found) return found;
        }
        return null;
    };

    const folder = findFolder(folderTree.folders, currentFolderId);
    return folder
        ? {
              dashboards: folder.dashboards,
              folders: folder.folders,
          }
        : { dashboards: [], folders: [] };
};

/**
 * Recursively finds the path to a folder by its ID in the folder tree.
 * @param {DashboardFolderTreeNodeT[]} folders - The list of folders to search in.
 * @param {number | null} targetFolderId - The ID of the folder to find.
 * @param {T[]} path - The current path being built.
 * @param {(folder: DashboardFolderTreeNodeT) => T} mapper - A function to map folder to desired type T.wa
 */
export const findFolderPath = <T extends string | DashboardFolderTreeNodeT>(
    folders: DashboardFolderTreeNodeT[],
    targetFolderId: number | null,
    path: T[],
    mapper: (folder: DashboardFolderTreeNodeT) => T,
): T[] => {
    for (const folder of folders) {
        const currentPath = [...path, mapper(folder)];
        if (folder.id === targetFolderId) {
            return currentPath;
        }
        const result = findFolderPath(
            folder.folders,
            targetFolderId,
            currentPath,
            mapper,
        );
        if (result.length > 0) return result;
    }
    return []; // return empty array if no path is found
};

/**
 * Retrieves the path of a folder by its ID from the folder tree.
 * @param {FolderTreeT | undefined} folderTree - The complete folder tree structure.
 * @param {number | null} currentFolderId - The ID of the folder to retrieve the path of.
 */
export const getFolderPath = (
    folderTree: FolderTreeT | undefined,
    currentFolderId: number | null,
) => {
    if (!folderTree || currentFolderId === null) {
        return [];
    }

    return findFolderPath(
        folderTree.folders,
        currentFolderId,
        [],
        (folder) => folder,
    );
};

/**
 * Filters dashboards and folders contained in the current folder by search value.
 * @param {FolderTreeT} currentFolder - The current folder tree structure to search in.
 * @param {string} searchValue - The value to search for in the folder names and dashboard titles.
 * @param {(dashboard: DashboardT, user: UserT) => boolean} isDashboardAllowedInView - A function that takes the dashboard and current user as arguments. Returning true if the dashboard should be displayed in the current view.
 * @param {UserT} user - The current user to filter the dashboards based on the view.
 */
export function searchInStructure<
    T extends DashboardFolderTreeNodeT | FolderTreeT,
>(
    currentFolder: T,
    searchValue: string,
    isDashboardAllowedInView: (dashboard: DashboardT, user: UserT) => boolean,
    user: UserT,
): { dashboardsTree: T; countMatchingDashboardsInOtherViews: number } {
    let countMatchingDashboardsInOtherViews = 0;
    const results: T = { ...currentFolder };
    results.folders = [];
    results.dashboards = [];
    // Search in current folder dashboards
    currentFolder.dashboards.forEach((dashboard) => {
        if (checkStringMatch(dashboard.title, searchValue)) {
            if (isDashboardAllowedInView(dashboard, user)) {
                results.dashboards.push(dashboard);
            } else {
                countMatchingDashboardsInOtherViews += 1;
            }
        }
    });

    // Search in current folder folders
    currentFolder.folders.forEach((folder) => {
        // Look recursively into each folder
        const childResults = searchInStructure(
            folder,
            searchValue,
            isDashboardAllowedInView,
            user,
        );
        if (!searchValue) {
            // If no search, we want to keep the treeview and display all folders
            results.folders.push(childResults.dashboardsTree);
        } else {
            if (checkStringMatch(folder.name, searchValue)) {
                // If folder name matches, we add the folder with all its content
                results.folders.push(folder);
            }
            // If search, we want to display only matching folders and everything flattened
            results.dashboards.push(...childResults.dashboardsTree.dashboards);
            results.folders.push(...childResults.dashboardsTree.folders);
        }
        countMatchingDashboardsInOtherViews +=
            childResults.countMatchingDashboardsInOtherViews;
    });

    return { dashboardsTree: results, countMatchingDashboardsInOtherViews };
}
